import { useCallback, useMemo } from "react";
import { SlidersHorizontal, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { sideFor } from "@/components/layout/SideMenu";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { HOME_SOURCES, HOME_CARDS, cleanHome, allowedSources, toggled } from "@/lib/homePrefs";

// 330: Můj Přehled — okénko se zaškrtávátky u karty Dnes. Nastavení drží účet (user.home_prefs),
// mění se hned a na server odejde chvíli po posledním kliknutí (víc kliknutí = jeden požadavek).
// Všichni odběratelé (osa, dlaždice, karty) čtou z user.home_prefs, takže se přepnou naráz.

let timer = null;
let latest = null;

export function useHomePrefs() {
  const { user, setUser } = useAuth();
  const prefs = useMemo(() => cleanHome(user?.home_prefs), [user?.home_prefs]);
  const menuKeys = useMemo(() => {
    const c = user ? sideFor(user) : { mods: [], firma: [] };
    return [...c.mods, ...(c.firma || [])].map((m) => m.key);
  }, [user]);
  const allowed = useMemo(() => allowedSources(menuKeys), [menuKeys]);

  const update = useCallback((fn) => {
    setUser((u) => {
      if (!u) return u;
      const next = fn(cleanHome(u.home_prefs));
      latest = next;
      return { ...u, home_prefs: next };
    });
    clearTimeout(timer);
    timer = setTimeout(() => {
      const body = latest;
      if (!body) return;
      api.put("/auth/home", body).catch(() => toast.error("Nastavení Přehledu se nepodařilo uložit. Zkus to prosím znovu."));
    }, 600);
  }, [setUser]);

  return {
    off: prefs.off.filter((k) => allowed.includes(k)),
    cardsOff: prefs.cards_off,
    allowed,
    toggleSource: (k) => update((p) => ({ ...p, off: toggled(p.off, k) })),
    toggleCard: (k) => update((p) => ({ ...p, cards_off: toggled(p.cards_off, k) })),
  };
}

function Row({ on, locked, label, hint, onClick, testid }) {
  if (locked) {
    return (
      <div className="flex items-center gap-3 px-2 py-2.5 rounded-[12px] opacity-60" aria-disabled="true" data-testid={testid}>
        <Lock size={16} className="shrink-0 text-muted-foreground" aria-hidden="true" />
        <span className="min-w-0 flex-1">
          <span className="block text-[14.5px] font-bold">{label}</span>
          <span className="block text-[12.5px] text-muted-foreground">Nemáš k tomu přístup</span>
        </span>
      </div>
    );
  }
  return (
    <button type="button" role="switch" aria-checked={on} onClick={onClick} data-testid={testid}
      className="w-full flex items-center gap-3 px-2 py-2.5 rounded-[12px] text-left hover:bg-secondary/60 min-h-[44px]">
      <span className="min-w-0 flex-1">
        <span className="block text-[14.5px] font-bold">{label}</span>
        {hint && <span className="block text-[12.5px] text-muted-foreground">{hint}</span>}
      </span>
      <span aria-hidden="true" className={`relative w-[34px] h-5 rounded-full shrink-0 transition-colors motion-reduce:transition-none ${on ? "bg-brand" : "bg-foreground/15"}`}>
        <span className="absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-[left] motion-reduce:transition-none" style={{ left: on ? 16 : 2 }} />
      </span>
    </button>
  );
}

// cards: ukázat i vypínače karet pod osou (jen počítač a tablet)
export function HomePrefsButton({ cards = false, className = "" }) {
  const { off, cardsOff, allowed, toggleSource, toggleCard } = useHomePrefs();
  const locked = HOME_SOURCES.some(([k]) => !allowed.includes(k));
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" aria-label="Přizpůsobit Přehled" title="Přizpůsobit Přehled" data-testid="home-prefs-btn"
          className={`w-10 h-10 rounded-full grid place-items-center text-muted-foreground hover:text-foreground hover:bg-secondary data-[state=open]:bg-secondary data-[state=open]:text-foreground ${className}`}>
          <SlidersHorizontal size={18} aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} data-testid="home-prefs"
        collisionPadding={{ top: 12, bottom: 96, left: 12, right: 12 }}   // dolní lišta telefonu
        className="w-[min(340px,calc(100vw-24px))] p-3 rounded-[18px] max-h-[var(--radix-popover-content-available-height)] overflow-y-auto">
        <b className="block px-2 text-[15px] font-bold">Co chci sledovat</b>
        <p className="px-2 pb-1.5 text-[12.5px] text-muted-foreground">Platí jen pro tebe, ukládá se samo.</p>
        {HOME_SOURCES.map(([k, label, hint]) => (
          <Row key={k} testid={`home-pref-${k}`} label={label} hint={hint} locked={!allowed.includes(k)} on={!off.includes(k)} onClick={() => toggleSource(k)} />
        ))}
        {cards && (<>
          <div className="px-2 pt-3 pb-1 text-[11.5px] font-bold uppercase tracking-wide text-muted-foreground">Karty na Přehledu</div>
          {HOME_CARDS.map(([k, label]) => (
            <Row key={k} testid={`home-card-${k}`} label={label} on={!cardsOff.includes(k)} onClick={() => toggleCard(k)} />
          ))}
        </>)}
        {locked && <p className="px-2 pt-2 text-[12.5px] text-muted-foreground">Zamčené věci ti může otevřít majitel v Týmu.</p>}
      </PopoverContent>
    </Popover>
  );
}
